/**
 * DealPriorityIndicator - Indicador visual compacto de prioridade
 * 
 * Características:
 * - Barra lateral colorida ou ícone
 * - Cores: Urgente=vermelho, Alta=laranja, Média=azul, Baixa=cinza
 * - Versão compacta para tabelas
 * 
 * Versão: 1.0.0
 */
import React from 'react';
import { cn } from '@/lib/utils';
import { DealPriority } from '@/hooks/useCrmDeals';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui/tooltip';

interface DealPriorityIndicatorProps {
  /** Prioridade do deal */
  priority: DealPriority;
  /** Tipo de indicador */
  type?: 'bar' | 'dot';
  /** Classes CSS adicionais */
  className?: string;
}

/**
 * Configuração de cores por prioridade
 */
const priorityConfig: Record<DealPriority, { color: string; label: string }> = {
  urgent: { color: 'bg-red-500', label: 'Urgente' },
  high: { color: 'bg-orange-500', label: 'Alta' },
  medium: { color: 'bg-blue-500', label: 'Média' },
  low: { color: 'bg-muted-foreground/40', label: 'Baixa' },
};

export function DealPriorityIndicator({
  priority,
  type = 'bar',
  className,
}: DealPriorityIndicatorProps) {
  const config = priorityConfig[priority];

  return (
    <TooltipProvider>
      <Tooltip>
        <TooltipTrigger asChild>
          {type === 'bar' ? (
            <div
              className={cn('w-1 h-full min-h-[40px] rounded-full', config.color, className)}
            />
          ) : (
            <div className={cn('h-2 w-2 rounded-full', config.color, className)} />
          )}
        </TooltipTrigger>
        <TooltipContent side="left" className="text-xs">
          <p>Prioridade: {config.label}</p>
        </TooltipContent>
      </Tooltip>
    </TooltipProvider>
  );
}
